package one.devsbox.www.ui.company.detail

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import one.devsbox.shared.ads.SharedIO
import one.devsbox.shared.devices.ApplicationType
import one.devsbox.shared.devices.WWW_APPS
import one.devsbox.www.tools.ArgsId
import one.devsbox.www.ui.widgets.*
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState

@Composable
@ComposableNavNode(ArgsId::class)
fun CompanyDUI() {
    with(CompanyDCtx.get()) {
        ContentCompanyDUI()
    }
}

@Composable
context(ctx: CompanyDCtx)
private fun ContentCompanyDUI() {
    Column(modifier = Modifier.fillMaxSize().padding(16.dp)) {
        Crossfade(targetState = ctx.vm.state) { state ->
            when (state) {
                is DataState.Idle -> Unit
                is DataState.Loading -> FullscreenLoadingUI()
                is DataState.Error -> {
                    FullscreenErrorUI(
                        message = state.message,
                        onBack = { ctx.action.back() },
                        onReload = { ctx.action.reload() }
                    )
                }

                is DataState.Result -> Box(modifier = Modifier.fillMaxSize()) {
                    val data = state.nnvl.company
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        Text(
                            text = data.name.uppercase(),
                            style = MaterialTheme.typography.headlineMedium,
                            fontWeight = FontWeight.Bold
                        )

                        Spacer(Modifier.height(8.dp))

                        BoxWithConstraints(
                            modifier = Modifier
                                .fillMaxWidth()
                                .border(
                                    BorderStroke(
                                        1.dp,
                                        MaterialTheme.colorScheme.outlineVariant
                                    ), RoundedCornerShape(8.dp)
                                )
                                .padding(16.dp)
                        ) {
                            val isWide = maxWidth >= 800.dp
                            if (isWide) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(32.dp)
                                ) {
                                    Column(modifier = Modifier.weight(1f)) {
                                        CompanyStatsSection(data)
                                    }
                                    Column(modifier = Modifier.weight(1f)) {
                                        ApplicationTypesSection(data)
                                    }
                                }
                            } else {
                                Column(
                                    modifier = Modifier.fillMaxWidth(),
                                    verticalArrangement = Arrangement.spacedBy(24.dp)
                                ) {
                                    CompanyStatsSection(data)
                                    ApplicationTypesSection(data)
                                }
                            }
                        }

                        if (data.id != ctx.me.info.company.working) {
                            Spacer(Modifier.height(16.dp))
                            Button(
                                onClick = { ctx.action.switchCompany() },
                                modifier = Modifier.fillMaxWidth().height(48.dp),
                                shape = RoundedCornerShape(4.dp)
                            ) {
                                Text(
                                    text = stringResource(Res.string.ui_company_dialog_btn_switch),
                                    fontWeight = FontWeight.Bold
                                )
                            }
                        }
                    }

                    ContentActions(
                        contentActions = ctx.vm.contentActions,
                        colorInactive = ctx.colorSecondaryContainer,
                        colorActive = ctx.colorPrimaryContainer
                    )
                }
            }
        }
    }

    if (ctx.vm.showDeleteConfirmation) {
        AlertDialog(
            onDismissRequest = { ctx.action.dismissDeleteConfirmation() },
            title = { Text(stringResource(Res.string.ui_company_dialog_delete_title)) },
            text = { Text(stringResource(Res.string.ui_company_dialog_delete_warning)) },
            confirmButton = {
                TextButton(
                    onClick = {
                        ctx.action.confirmDelete()
                    }
                ) {
                    Text(
                        text = stringResource(Res.string.ui_company_dialog_btn_delete),
                        color = MaterialTheme.colorScheme.error
                    )
                }
            },
            dismissButton = {
                TextButton(onClick = { ctx.action.dismissDeleteConfirmation() }) {
                    Text(stringResource(Res.string.ui_company_dialog_btn_cancel))
                }
            }
        )
    }

    if (ctx.vm.showDeleteError) {
        AlertDialog(
            onDismissRequest = { ctx.action.dismissDeleteError() },
            title = { Text(stringResource(Res.string.ui_devices_edit_deviceeui_error)) },
            text = { Text(ctx.vm.deleteErrorMessage) },
            confirmButton = {
                TextButton(onClick = { ctx.action.dismissDeleteError() }) {
                    Text(stringResource(Res.string.ui_devices_edit_deviceeui_ok))
                }
            }
        )
    }
}

@Composable
private fun CompanyStatsSection(company: SharedIO.CompanySimple) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Text(
            text = "COUNTERS",
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.8f)
        )
        Spacer(Modifier.height(12.dp))
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            StatCard(
                "DEVICES",
                company.counter.device.toString().padStart(2, '0'),
                Modifier.weight(1f)
            )
            StatCard(
                "LOCATIONS",
                company.counter.location.toString().padStart(2, '0'),
                Modifier.weight(1f)
            )
            StatCard("USERS", company.counter.user.toString().padStart(2, '0'), Modifier.weight(1f))
        }
    }
}

@Composable
private fun StatCard(label: String, value: String, modifier: Modifier = Modifier) {
    Column(
        modifier = modifier
            .border(
                BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
                RoundedCornerShape(4.dp)
            )
            .padding(12.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Spacer(Modifier.height(4.dp))
        Text(
            text = value,
            fontSize = 24.sp,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.primary,
            fontFamily = FontFamily.Monospace
        )
    }
}

@Composable
private fun ApplicationTypesSection(company: SharedIO.CompanySimple) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Text(
            text = "APPLICATION TYPES",
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.8f)
        )
        Spacer(Modifier.height(8.dp))
        ApplicationType.WWW_APPS.forEach { appType ->
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp)
            ) {
                Checkbox(
                    checked = (company.appTypeMask and appType.mask) != 0,
                    onCheckedChange = null,
                    enabled = false
                )
                Spacer(Modifier.width(8.dp))
                Text(
                    text = stringResource(appType.label),
                    style = MaterialTheme.typography.bodyLarge
                )
            }
        }
    }
}

